import { Controller } from '@hotwired/stimulus';
import { get } from '@rails/request.js';
import Sortable from 'sortablejs';

export default class extends Controller {
  static targets = [
    'contentGrid',
    'contentCheckbox',
    'contentContainer',
    'sortableContentGrid',
    'contentIdsInput',
    'previewContentIdInput',
    'contentIdInput',
    'freePreviewPlaceholder',
    'freePreviewContainer',
    'freePreviewImage',
    'libraryFrame',
    'submitButton',
    'priceInput',
    'freePreviewItem',
    'priceToViewHtml',
    'resetPriceButton',
    'priceModal',
    'priceModalInput',
  ];

  static values = {
    selectedContentIds: { type: Array, default: [] },
    selectedContentUrl: { type: String },
    enableFreePreview: { type: Boolean, default: false },
  };

  connect() {
    this.initializeState();
    this.connectSortable();
    this.initializeSelectedContents();
    this.updateSubmitButtonState();
    this.refreshUrlIfContentAvailableInUrl();

    if (this.hasPriceInputTarget && this.enableFreePreviewValue) {
      this.setupFreePreview();
    }
  }

  disconnect() {
    this.destroySortable();
  }

  initializeState() {
    this.selectedContents = [];
    this.currentView = 'grid';
    this.currentSort = 'newest';
    this.currentTypeFilter = 'all';
    this.currentSizeFilter = 'all';
  }

  // Price modal functionality
  openPriceModal() {
    if (this.hasPriceModalTarget && this.hasPriceModalInputTarget) {
      let value = this.priceInputTarget.value || '';
      if (value === '0' || value === '0.0') {
        value = '';
      }
      this.priceModalInputTarget.value = value;
      this.priceModalTarget.showModal();
      setTimeout(() => {
        this.priceModalInputTarget.focus();
      }, 100);
    }
  }

  savePriceFromModal() {
    if (this.hasPriceInputTarget && this.hasPriceModalInputTarget) {
      this.priceInputTarget.value = this.priceModalInputTarget.value;
      this.toggleFreePreviewVisibility();
      this.priceModalTarget.close();
    }
  }

  resetPrice() {
    if (this.hasPriceInputTarget && this.hasPriceModalInputTarget) {
      this.priceModalInputTarget.value = '0';
      this.priceInputTarget.value = '0';
      this.toggleFreePreviewVisibility();
    }
  }

  // Price and free preview functionality
  setupFreePreview() {
    this.priceInputTarget.addEventListener('input', () => {
      this.toggleFreePreviewVisibility();
    });

    this.toggleFreePreviewVisibility();
  }

  toggleFreePreviewVisibility() {
    if (!this.hasPriceInputTarget || !this.hasFreePreviewItemTarget) return;

    const priceValue = parseFloat(this.priceInputTarget.value) || 0;

    if (priceValue > 0) {
      this.freePreviewItemTarget.classList.remove('hidden');
      this.priceToViewHtmlTarget.innerText = `$${priceValue}`;
      this.resetPriceButtonTarget.classList.remove('hidden');
    } else {
      this.freePreviewItemTarget.classList.add('hidden');
      this.priceToViewHtmlTarget.innerText = 'Free';
      this.resetPriceButtonTarget.classList.add('hidden');
    }
  }

  freePreviewItemTargetConnected() {
    if (!this.hasPriceInputTarget) return;

    const priceValue = parseFloat(this.priceInputTarget.value) || 0;
    this.toggleFreePreviewVisibility();

    if (priceValue > 0) {
      this.priceToViewHtmlTarget.innerText = `$${priceValue}`;
    } else {
      this.priceToViewHtmlTarget.innerText = 'Free';
    }
  }

  // URL handling for content
  refreshUrlIfContentAvailableInUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('content_ids')) {
      // Remove the parameter from URL without page refresh
      window.history.replaceState({}, '', window.location.pathname);
    }
  }

  // Modal management
  openModal(event) {
    this.configureModal('false', 'ADD MEDIA');
    this.showModalAndLoadContent();
  }

  openFreePreviewModal(event) {
    this.configureModal('true', 'ADD FREE PREVIEW');
    this.showModalAndLoadContent();
  }

  configureModal(freePreviewMode, buttonText) {
    const modal = this.getContentModal();
    modal.dataset.freePreviewMode = freePreviewMode;
    this.submitButtonTarget.innerHTML = buttonText;
  }

  showModalAndLoadContent() {
    const modal = this.getContentModal();
    modal.showModal();
    this.loadContent();
    this.clearSelections();
  }

  closeModal() {
    const modal = this.getContentModal();
    modal.close();
  }

  getContentModal() {
    return document.getElementById('content-selector-modal');
  }

  isFreePreviewMode() {
    const modal = this.getContentModal();
    return modal && modal.dataset.freePreviewMode === 'true';
  }

  isSingleContentMode() {
    return this.hasContentIdInputTarget && !this.hasContentIdsInputTarget;
  }

  loadContent() {
    const frame = this.libraryFrameTarget;
    frame.classList.remove('hidden');

    if (frame.getAttribute('src') === '') {
      frame.setAttribute('src', frame.dataset.src);
    }
  }

  // Content selection
  selectContent(event) {
    const contentItem = event.currentTarget;
    const contentId = contentItem.dataset.contentId;
    const checkbox = contentItem.querySelector("input[type='checkbox']");

    if (this.isFreePreviewMode() || this.isSingleContentMode()) {
      this.handleSingleContentSelection(contentId, checkbox);
    } else {
      this.handleMultipleSelection(contentId, checkbox);
    }

    this.updateSubmitButtonState();
  }

  handleSingleContentSelection(contentId, checkbox) {
    if (this.selectedContents.includes(contentId)) {
      this.selectedContents = [];
      checkbox.checked = false;
    } else {
      this.clearCheckboxes();
      this.selectedContents = [contentId];
      checkbox.checked = true;
    }
  }

  handleMultipleSelection(contentId, checkbox) {
    if (this.selectedContents.includes(contentId)) {
      this.selectedContents = this.selectedContents.filter(
        (id) => id !== contentId
      );
      checkbox.checked = false;
    } else {
      this.selectedContents.push(contentId);
      checkbox.checked = true;
    }
  }

  clearCheckboxes() {
    if (this.hasContentCheckboxTarget) {
      this.contentCheckboxTargets.forEach((cb) => {
        cb.checked = false;
      });
    }
  }

  clearSelections() {
    this.clearCheckboxes();
    this.selectedContents = [];
    this.updateSubmitButtonState();
  }

  // Confirm selection
  confirmSelection() {
    if (this.selectedContents.length === 0) return;

    if (this.isFreePreviewMode() || this.isSingleContentMode()) {
      this.confirmFreePreviewSelection();
    } else {
      this.confirmRegularSelection();
    }

    this.clearSelections();
    this.closeModal();
  }

  confirmFreePreviewSelection() {
    if (this.hasPreviewContentIdInputTarget) {
      this.previewContentIdInputTarget.value = this.selectedContents[0];
    } else if (this.hasContentIdInputTarget) {
      this.contentIdInputTarget.value = this.selectedContents[0];
      this.contentIdInputTarget.dispatchEvent(new Event('change'));
    }
    this.fetchSelectedContent(true);
  }

  confirmRegularSelection() {
    this.mergeWithExistingSelections();
    this.updateContentIdsInput();
    this.fetchSelectedContent(false);
  }

  mergeWithExistingSelections() {
    let existingContentIds = [];
    if (this.hasContentIdsInputTarget && this.contentIdsInputTarget.value) {
      existingContentIds = this.contentIdsInputTarget.value.split(',');
    }

    this.selectedContents = [
      ...new Set([...existingContentIds, ...this.selectedContents]),
    ];
  }

  updateContentIdsInput() {
    if (this.hasContentIdsInputTarget) {
      this.contentIdsInputTarget.value = this.selectedContents.join(',');
    }
  }

  // Content removal
  removeContent(event) {
    const contentId = event.currentTarget.dataset.contentId;
    this.removeContentFromInput(contentId);
    this.updateVisibilityAfterRemoval();
    event.currentTarget.closest('div').remove();
    this.updateSubmitButtonState();
  }

  removeContentFromInput(contentId) {
    this.contentIdsInputTarget.value = this.contentIdsInputTarget.value
      .split(',')
      .filter((id) => id !== contentId)
      .join(',');
  }

  updateVisibilityAfterRemoval() {
    if (this.contentIdsInputTarget.value.split(',')[0] === '') {
      this.hideMediaPreviewContainer();
      if (this.hasFreePreviewContainerTarget) {
        this.freePreviewContainerTarget.classList.add('hidden');
      }
    }
  }

  removeFreePreviewContent(event) {
    this.previewContentIdInputTarget.value = '';
    this.freePreviewPlaceholderTarget.classList.remove('hidden');
    this.freePreviewImageTarget.classList.add('hidden');
  }

  // Content fetching
  async fetchSelectedContent(isPreview = false) {
    if (this.selectedContents.length === 0 && !isPreview) {
      this.hideMediaPreviewContainer();
      return;
    }

    const url = this.buildContentFetchUrl(isPreview);
    const response = await get(url, { responseKind: 'turbo-stream' });

    if (response.ok) {
      setTimeout(() => {
        this.scrollChatMessages();
        this.reconnectSortable();
      }, 500);
    }
  }

  scrollChatMessages() {
    const container = document.getElementById('chat-messages');
    if (container) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    }
  }

  buildContentFetchUrl(isPreview) {
    let url = `${this.selectedContentUrlValue}?`;

    // Only add content_ids if we have a valid input target and value
    if (this.hasContentIdsInputTarget) {
      url += `content_ids=${this.contentIdsInputTarget.value}`;
    }

    if (this.hasContentIdInputTarget) {
      url += `content_id=${this.contentIdInputTarget.value}`;
    }

    // Only add preview_content_id if we have a valid input target
    if (this.hasPreviewContentIdInputTarget) {
      url += `&preview_content_id=${this.previewContentIdInputTarget.value}`;
    }

    return url;
  }

  // Sortable functionality
  initializeSelectedContents() {
    if (!this.hasSortableContentGridTarget) return;

    const existingSortableItems = Array.from(
      this.sortableContentGridTarget.children
    )
      .filter((el) => el.dataset.sortableId)
      .map((el) => el.dataset.sortableId);

    if (existingSortableItems.length > 0) {
      this.selectedContents = existingSortableItems;
      this.updateContentIdsInput();
    }
  }

  connectSortable() {
    if (!this.hasSortableContentGridTarget) return;

    this.sortable = Sortable.create(this.sortableContentGridTarget, {
      animation: 150,
      ghostClass: 'opacity-0',
      delayOnTouchOnly: true,
      scroll: true,
      delay: 100,
      onEnd: this.sortableEnd.bind(this),
    });
  }

  sortableEnd(event) {
    const newOrder = Array.from(this.sortableContentGridTarget.children)
      .filter((el) => el.dataset.sortableId)
      .map((el) => el.dataset.sortableId);

    this.selectedContents = newOrder;
    this.updateContentIdsInput();
  }

  reconnectSortable() {
    this.destroySortable();
    this.connectSortable();
  }

  destroySortable() {
    if (this.sortable) {
      this.sortable.destroy();
    }
  }

  // Content sorting
  sortBy(event) {
    this.currentSort = event.currentTarget.dataset.sort;
    this.sortContentItems();
  }

  sortContentItems() {
    const contentItems = Array.from(
      this.libraryFrameTarget.querySelectorAll('.content-item')
    );

    contentItems.sort((a, b) => {
      const aId = parseInt(a.dataset.contentId);
      const bId = parseInt(b.dataset.contentId);

      return this.currentSort === 'newest' ? bId - aId : aId - bId;
    });

    this.libraryFrameTarget.innerHTML = '';
    contentItems.forEach((item) => {
      this.libraryFrameTarget.appendChild(item);
    });
  }

  // UI state management
  updateSubmitButtonState() {
    if (!this.hasSubmitButtonTarget) return;

    if (this.selectedContents.length === 0) {
      this.submitButtonTarget.classList.add('btn-disabled');
    } else {
      this.submitButtonTarget.classList.remove('btn-disabled');
    }
  }

  getMediaPreviewContainer() {
    return document.querySelector('[data-media-preview]');
  }

  hideMediaPreviewContainer() {
    const previewContainer = this.getMediaPreviewContainer();
    if (previewContainer) {
      previewContainer.classList.add('hidden');
    }
  }
}
